export const PowerThresholds = {
  GW: 1e6, // 1 GW = 1,000,000 kW
  MW: 1e3, // 1 MW = 1,000 kW
  kW: 1    // 1 kW = 1 kW
}

export function formatterPower(power) {
  const unit = getPowerUnitLevel(power)
  const formattedValue = (power / PowerThresholds[unit]).toFixed(2);
  return formattedValue + ' ' + unit
}

export function getPowerUnitLevel(power) {
  const units = Object.keys(PowerThresholds);
  for (let unit of units) {
    if (power >= PowerThresholds[unit]) {
      return unit
    }
  }
  return 'kW'
}

export const CapacityThresholds = {
  GWh: 1e6, // 1 GWh = 1,000,000 kWh
  MWh: 1e3, // 1 MWh = 1,000 kWh
  kWh: 1    // 1 kWh = 1 kWh
}

export function formatterCapacity(capacity) {
  const unit = getCapacityUnitLevel(capacity)
  const formattedValue = (capacity / CapacityThresholds[unit]).toFixed(2);
  return formattedValue + ' ' + unit
}

export function getCapacityUnitLevel(capacity) {
  const units = Object.keys(CapacityThresholds);
  for (let unit of units) {
    if (capacity >= CapacityThresholds[unit]) {
      return unit
    }
  }
  return 'kWh'
}

/**
 * 格式化功率值，返回数值和单位
 * @param {number} power 功率值
 * @param {number} frac 保留小数位数
 * @returns {{value: string, unit: string}} 包含数值和单位的对象
 */
export function fmtp(power, frac = 2) {
  const unit = getPowerUnitLevel(power)
  const num = Number(power)
  const formattedValue = (power === null || power === undefined || power === '' || Number.isNaN(num))
    ? '--'
    : (num / PowerThresholds[unit]).toFixed(frac);

  return {
    value: formattedValue,
    unit: unit,
    toString() {
      return formattedValue + ' ' + unit
    }
  }
}


/**
 * 格式化以 W 为单位的功率值，返回数值和单位
 * @param {number} watt 功率值（单位 W）
 * @param {number} frac 保留小数位数
 * @returns {{value: string, unit: string}} 包含数值和单位的对象
 */
export function fmtw(watt, frac = 2) {
  const num = Number(watt)
  if (watt === null || watt === undefined || watt === '' || Number.isNaN(num)) {
    return { value: '--', unit: 'W' }
  }
  const abs = Math.abs(num)
  const unit = abs >= 1e6 ? 'MW' : abs >= 1e3 ? 'kW' : 'W'
  const scale = { MW: 1e6, kW: 1e3, W: 1 }[unit]
  return { value: (num / scale).toFixed(frac), unit }
}

/**
 * 格式化电能，返回数值和单位
 * @param {number} capacity 电能值
 * @param {number} frac 保留小数位数
 * @returns {{value: string, unit: string}} 包含数值和单位的对象
 */
export function fmte(capacity, frac = 2) {
  const unit = getCapacityUnitLevel(capacity)
  const num = Number(capacity)
  const formattedValue = (capacity === null || capacity === undefined || capacity === '' || Number.isNaN(num))
    ? '--'
    : (num / CapacityThresholds[unit]).toFixed(frac);
  return {
    value: formattedValue,
    unit: unit,
    toString() {
      return formattedValue + ' ' + unit
    }
  }
}


/**
 * 支持升位的千级单位表
 */
const KiloUnits = {
  'kW': { M: 'MW', G: 'GW' },
  'kWh': { M: 'MWh', G: 'GWh' },
  'kVar': { M: 'MVar', G: 'GVar' },
  'kΩ': { M: 'MΩ', G: 'GΩ' },
}

/**
 * 查找单位对应的升位目标，忽略大小写差异
 * （Ω 有 U+2126 和 U+03A9 两种码点，小写后都是 ω，故大小写比较也能兼容）
 */
function matchKiloUnit(unit) {
  const u = String(unit ?? '').trim()
  if (!u) return null
  if (KiloUnits[u]) return KiloUnits[u]
  const lower = u.toLowerCase()
  const key = Object.keys(KiloUnits).find(k => k.toLowerCase() === lower)
  return key ? KiloUnits[key] : null
}

/**
 * kW / kWh / kVar / kΩ 按量级升位
 * 1000 kW → 1.00 MW，1000000 kW → 1.00 GW；不足 1000 或其它单位保持原样
 * @param {number|string} value 数值
 * @param {string} unit 原始单位
 * @param {number} frac 保留小数位数
 * @returns {{value: string|number, unit: string}} 包含数值和单位的对象
 */
export function fmtku(value, unit, frac = 2) {
  const num = Number(value)
  const invalid = value === null || value === undefined || value === '' || Number.isNaN(num)
  const target = matchKiloUnit(unit)
  if (invalid || !target) {
    return { value, unit }
  }
  const abs = Math.abs(num)
  const level = abs >= 1e6
    ? { unit: target.G, scale: 1e6 }
    : abs >= 1e3
      ? { unit: target.M, scale: 1e3 }
      : null
  if (!level) return { value, unit }
  return { value: (num / level.scale).toFixed(frac), unit: level.unit }
}

/**
 * 格式化收益金额，固定保留小数位（不做单位升位，单位由调用方在模板里写死）
 * 空值口径与 fmtp / fmte 保持一致：null / undefined / '' / NaN 一律返回 '--'
 * @param {number|string} value 金额
 * @param {number} frac 保留小数位数
 * @returns {string} 格式化后的金额字符串
 */
export function fmtIncome(value, frac = 2) {
  const num = Number(value)
  if (value === null || value === undefined || value === '' || Number.isNaN(num)) {
    return '--'
  }
  return num.toFixed(frac)
}

/**
 * 格式化日期为本地时间字符串
 * @param {Date} date - 日期对象
 * @returns {string} 格式化后的时间字符串 (YYYY-MM-DD HH:mm:ss)
 */
export function formatLocalDateTime(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

/**
 * 获取默认时间参数（近一年）
 * @returns {Object} 包含 startTime 和 endTime 的对象
 */
export function getDefaultTimeParams() {
  const now = new Date()
  const endTime = formatLocalDateTime(now)

  const startDate = new Date(now)
  startDate.setFullYear(startDate.getFullYear() - 1)
  const startTime = formatLocalDateTime(startDate)

  return {
    startTime,
    endTime,
    area: ''
  }
}
